import React from 'react';
import moment from 'moment';
import { Box, Grid } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    TableCell,
    TextField,
    TextareaAutosize,
    makeStyles,
    Typography,
    Hidden,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    FormControlLabel,
    Checkbox,
} from '@material-ui/core';
import { useIntl } from 'react-intl';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import CustomDateTimePicker from '../../../UIComponent/CustomDateTimePicker';
import { isDesktop, isMobile, isTablet } from "react-device-detect";
import { DropzoneArea } from 'material-ui-dropzone';
import { breakpoints, pxToRem } from '../../../UIComponent/CommonStyle';
import { MuiThemeProvider, createTheme } from '@material-ui/core/styles';
import MuiAlert from '@material-ui/lab/Alert';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import CancelIcon from '@material-ui/icons/Cancel';
import { checkIsTabletDevice } from '../../../Function/globalFunction';

function Alert(props) {
    return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const useStyles = makeStyles(() => ({
    descriptionCell: {
        wordWrap: 'break-word',
        borderBottom: 'none',
        padding: 0,
        whiteSpace: 'pre-line',
        minWidth: '100%',
    },
    errorMessage: {
        color: 'rgb(244, 67, 54)',
        position: 'absolute',
        fontSize: '12px',
    },
    errorMessageDesc: {
        marginTop: '-5px',
    },
    fontSize: {
        fontSize: '12.25px',
    },
    dateWidth: {
        minWidth: isDesktop ? 110 : 'auto',
    },
    hoursWidth: {
        minWidth: isDesktop ? 110 : 'auto',
    },
    required: {
        color:'red',
    },
    dropzone: {
        minHeight: '100%',
        marginTop: !isDesktop ? undefined : -5,
    },
    dropzoneParagraphClass: {
        padding: '5px 5px 0 5px',
        fontSize: pxToRem(14),
        [breakpoints.up("md")]: {
            fontSize: pxToRem(16),
        },
        margin: 0,
    },
    attachment: {
        borderStyle: 'solid',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        borderRadius: '5px'
    },
    label: {
        marginRight: 0,
        '& [class*="MuiFormControlLabel-label"]': {
            fontSize: '11px',
            whiteSpace: 'nowrap',
            lineHeight: 'normal',
        },
    },
}));

const StyledDropzone = createTheme({
    overrides: {
        MuiDropzoneArea: {
            icon: {
                width: 20,
                height: 20,
            },
        }
    }
});

export function setColumns(
    rows,
    handleInputChange,
    handleRemoveRow,
    calculateHours,
    errorMessages,
    mode,
    disabled,
    handleUpload,
    handleAttachmentClick,
    showRemoveFile,
    handleAttachmentDelete,
    handleCheckboxChange
) {
    const intl = useIntl();
    const classes = useStyles();
    const isSubmitMode = mode === window.global.submitMode;
    const isTabletDevice = checkIsTabletDevice() || isTablet;

    const columns = [
        {
            name: 'from',
            label:
                intl.formatMessage({
                    id: 'common.from',
                }),
            options: {
                sort: true,
                filter: true,
                filterType: 'textField',
                setCellProps: () => ({
                    style: {
                        borderBottom: !isDesktop ? 'none' : undefined,
                        padding: !isDesktop ? '12px 16px' : undefined,
                        width: isDesktop ? '15.5%' : isTabletDevice ? '25%' : '100%',
                    }
                }),
                customHeadLabelRender: () => ( 
                    <span>
                        {intl.formatMessage({id: 'common.from'})}
                        {isSubmitMode && <span className={classes.required}>*</span>}
                    </span> 
                ),
                customBodyRender: (value, tableMeta) => {
                    const from = moment(value, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm');
                    const today = moment();

                    if (isSubmitMode) {
                        return (
                            <>
                                <CustomDateTimePicker
                                    id='comp-leave-from'
                                    value={moment(
                                        rows[tableMeta.rowIndex].from,
                                        'YYYY-MM-DD HH:mm'
                                    )}
                                    field='from'
                                    index={tableMeta.rowIndex}
                                    handleInputChange={handleInputChange}
                                    disabled={disabled}
                                    maxDateTime={today || null}
                                />
                                {errorMessages[tableMeta.rowIndex]?.from && (
                                    <Typography className={classes.errorMessage}>
                                        {errorMessages[tableMeta.rowIndex].from}
                                    </Typography>
                                )}
                            </>
                        );
                    } else {
                        return (
                            <Typography className={`${classes.fontSize} ${classes.dateWidth}`}>
                                {from}
                            </Typography>
                        );
                    }
                },
            },
        },
        {
            name: 'to',
            label:
                intl.formatMessage({
                    id: 'common.to',
                }),
            options: {
                sort: true,
                filter: true,
                filterType: 'textField',
                setCellProps: () => ({
                    style: {
                        borderBottom: !isDesktop ? 'none' : undefined,
                        padding: !isDesktop ? '12px 16px' : undefined,
                        width: isDesktop ? '15.5%' : isTabletDevice ? '25%' : '100%',
                    }
                }),
                customHeadLabelRender: () => ( 
                    <span>
                        {intl.formatMessage({id: 'common.to'})}
                        {isSubmitMode && <span className={classes.required}>*</span>}
                    </span> 
                ),
                customBodyRender: (value, tableMeta) => {
                    const from = moment(rows[tableMeta.rowIndex].from);
                    const to = moment(value, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm');
                    const today = moment();

                    if (isSubmitMode) {
                        return (
                            <>
                                <CustomDateTimePicker
                                    id='comp-leave-to'
                                    value={moment(
                                        rows[tableMeta.rowIndex].to,
                                        'YYYY-MM-DD HH:mm'
                                    )}
                                    field='to'
                                    index={tableMeta.rowIndex}
                                    handleInputChange={handleInputChange}
                                    disabled={disabled}
                                    minDateTime={from || null}
                                    maxDateTime={today || null}
                                />
                                {errorMessages[tableMeta.rowIndex]?.to && (
                                    <Typography className={classes.errorMessage}>
                                        {errorMessages[tableMeta.rowIndex].to}
                                    </Typography>
                                )}
                            </>
                        );
                    } else {
                        return (
                            <Typography className={`${classes.fontSize} ${classes.dateWidth}`}>
                                {to}
                            </Typography>
                        );
                    }
                },
            },
        },
        {
            name: 'hours',
            label: intl.formatMessage({
                id: 'comp_leave.number_of_hours',
            }),
            options: {
                filter: false,
                sort: true,
                setCellProps: () => ({
                    style: {
                        borderBottom: !isDesktop ? 'none' : undefined,
                        padding: !isDesktop ? '12px 16px' : undefined,
                        width: isTabletDevice ? '5%' : isDesktop ? '5%' : '100%',
                    }
                }),
                customBodyRender: (value, tableMeta, updateValue) => {
                    const rowIndex = tableMeta.rowIndex;
                    const row = rows[rowIndex];
                    const from = row.from;
                    const to = row.to || 0;
                    const includeMealHour = row.includeMealHour;
                    const rawHours = calculateHours(from, to, false);
                    const adjustedHours = calculateHours(from, to, includeMealHour);

                    return (
                        <div>
                            <Typography className={`${classes.fontSize} ${classes.hoursWidth}`}>
                                {isSubmitMode ? adjustedHours : value}
                            </Typography>
                                <FormControlLabel 
                                    control={
                                        <Checkbox
                                            checked={includeMealHour}
                                            onChange={(e) => handleCheckboxChange(rowIndex, e.target.checked)}
                                            size="small"
                                            disabled={!isSubmitMode || rawHours < 1.5}
                                            color="primary"
                                        />
                                    } 
                                    label={intl.formatMessage({id: 'comp_leave.include_meal_hour'})} 
                                    className={classes.label}
                                />
                        </div>
                    );
                },
            },
        },
        {
            name: 'description',
            label:
                intl.formatMessage({
                    id: 'comp_leave.justification',
                }),
            options: {
                sort: true,
                filter: true,
                filterType: 'textField',
                setCellProps: () => ({
                    style: {
                        borderBottom: !isDesktop ? 'none' : undefined,
                        padding: !isDesktop ? '12px 16px' : undefined,
                        width: isTabletDevice ? '30%' : isDesktop ? '40%' : '100%',
                    }
                }),
                customHeadLabelRender: (columnMeta) => ( 
                    <span>
                        {intl.formatMessage({id: 'comp_leave.justification'})}
                        {isSubmitMode && <span className={classes.required}>*</span>}
                    </span> 
                ),
                customBodyRender: (value, tableMeta, updateValue) => {
                    if (mode === window.global.submitMode) {
                        return (
                            <>
                                <Typography className={`${classes.descriptionCell}`}>
                                    <TextareaAutosize
                                        id='comp-leave-description'
                                        aria-label='description'
                                        value={rows[tableMeta.rowIndex].description}
                                        onChange={(e) =>
                                            handleInputChange(
                                                tableMeta.rowIndex,
                                                'description',
                                                e.target.value
                                            )
                                        }
                                        minRows={3}
                                        style={{ width: '100%' }}
                                        disabled={disabled}
                                    />
                                </Typography>
                                {errorMessages[tableMeta.rowIndex]?.description && (
                                    <Typography className={`${classes.errorMessage} ${classes.errorMessageDesc}`}>
                                        {errorMessages[tableMeta.rowIndex].description}
                                    </Typography>
                                )}
                            </>
                        );
                    } else {
                        return (
                            <Typography className={`${classes.descriptionCell} ${classes.fontSize}`}>
                                {value}
                            </Typography>
                        );
                    }
                },
            },
        },
        {
            name: 'attachment',
            label:
                intl.formatMessage({
                    id: 'comp_leave.attachment',
                }),
            options: {
                sort: false,
                filter: false,
                setCellProps: () => ({
                    style: {
                        borderBottom: !isDesktop ? 'none' : undefined,
                        padding: !isDesktop ? '12px 16px' : undefined,
                        width: isTabletDevice || isDesktop ? '25%' : '100%',
                    }
                }),
                customHeadLabelRender: (columnMeta) => ( 
                    <span>
                        {intl.formatMessage({id: 'comp_leave.attachment'})}
                        {isSubmitMode && <span> ({intl.formatMessage({id: 'comp_leave.optional'})})</span>}
                    </span> 
                ),
                customBodyRender: (value, tableMeta, updateValue) => {
                    const rowIndex = tableMeta.rowIndex;
                    const attachment = rows[rowIndex].attachment;
                
                    if (mode === window.global.submitMode) {
                        return (
                                <Grid item xs={12}>
                                    {attachment && attachment.length > 0 ? (
                                        <>
                                            <List>
                                                {attachment.map((file, idx) =>
                                                    <ListItem button key={idx} onClick={() => handleAttachmentClick(file.s3_url)} className={classes.attachment}>
                                                        <ListItemIcon>
                                                            <InsertDriveFileIcon />
                                                        </ListItemIcon>
                                                        <Hidden smDown>
                                                            <ListItemText primary={file.filename} />
                                                        </Hidden>
                                                        <Hidden mdUp>
                                                            <ListItemText primary={(file.filename).replace(/(.{30})..+/, "$1…")} />
                                                        </Hidden>
                                                        {showRemoveFile &&
                                                            <ListItemSecondaryAction>
                                                                <IconButton edge="end" aria-label="delete" onClick={() => handleAttachmentDelete(rowIndex, file.file_id)}>
                                                                    <CancelIcon />
                                                                </IconButton>
                                                            </ListItemSecondaryAction>
                                                        }
                                                    </ListItem>
                                                )}
                                            </List>
                                        </>
                                    ) : (
                                        <div className={classes.dropzoneDiv} style={disabled ? { pointerEvents: "none" } : {}}>
                                            <MuiThemeProvider theme={StyledDropzone}>
                                                <DropzoneArea
                                                    value={rows[rowIndex].attachment}
                                                    onChange={(files) => handleUpload(files, rowIndex)}
                                                    acceptedFiles={[
                                                        'image/*',
                                                        'image/heic',
                                                        'application/pdf',
                                                        'application/msword',
                                                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                                                        'application/vnd.ms-excel',
                                                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                                                        'text/csv',
                                                        'application/vnd.ms-powerpoint',
                                                        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                                                        'application/rtf',
                                                        'text/plain'
                                                    ]}
                                                    filesLimit={100}
                                                    showPreviewsInDropzone={false}
                                                    showPreviews={false}
                                                    showFileNamesInPreview={false}
                                                    dropzoneClass={classes.dropzone}
                                                    dropzoneParagraphClass={classes.dropzoneParagraphClass}
                                                    maxFileSize={20971520}
                                                    dropzoneText={intl.formatMessage({ id: 'new_application.dropzone_message' })}
                                                    previewText={intl.formatMessage({ id: 'new_application.preview' })}
                                                    showAlerts={['error', 'info']}
                                                />
                                            </MuiThemeProvider>
                                        </div>
                                    )}
                                </Grid>
                        );
                    } else {
                        return attachment && attachment.length > 0 ? (
                            <List>
                                {attachment.map((file, idx) =>
                                    <ListItem button key={idx} onClick={() => handleAttachmentClick(file.s3_url)} className={classes.attachment}>
                                        <ListItemIcon>
                                            <InsertDriveFileIcon />
                                        </ListItemIcon>
                                        <Hidden smDown>
                                            <ListItemText primary={file.filename} />
                                        </Hidden>
                                        <Hidden mdUp>
                                            <ListItemText primary={(file.filename).replace(/(.{30})..+/, "$1…")} />
                                        </Hidden>
                                    </ListItem>
                                )}
                            </List>
                        ) : (
                            <Typography className={classes.fontSize}>
                                N/A
                            </Typography>
                        );
                    }
                },
            },
        },
    ];

    if (mode === window.global.submitMode) {
        columns.push({
            name: '',
            label: '',
            options: {
                sort: true,
                setCellProps: () => ({
                    style: {
                        borderBottom: !isDesktop ? 'none' : undefined,
                        padding: 0,
                        textAlign: !isDesktop ? 'center' : undefined,
                        margin: !isDesktop ? '-30px 0 10px 0' : undefined,
                    }
                }),
                customBodyRender: (value, tableMeta, updateValue) => {
                    return (
                        <IconButton
                            id='comp-leave-delete'
                            onClick={() => handleRemoveRow(tableMeta.rowIndex)}
                        >
                            <DeleteIcon />
                        </IconButton>
                    );
                },
            },
        });
    }

    return columns;
}
